import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Icon,
  Image,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { PloyLogoV2 } from "components/Logo";
import { useMemberAuthStore } from "modules/members/auth";
import { getSubdomain } from "modules/members/api";
import { safeRedirectPath } from "modules/members/safeRedirect";
import { useCanonicalPortalRedirect } from "modules/members/useCanonicalPortalRedirect";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MFMembers from "state/members/membersState";
import {
  loginHeading,
  offeredProviders,
  requestedProvider,
} from "./loginProviders";
import { openSignInWindow, redirectToSignIn, signInRequestPath } from "./oauth";
import { rememberReturnPath } from "./returnPath";

const SignIn = () => {
  const navigate = useNavigate();
  const subdomain = getSubdomain();
  const data = MFMembers.useGetPortalConfigBySubdomain(subdomain);
  const [search, setSearch] = useSearchParams();
  const redirect = safeRedirectPath(search.get("redirectTo"));
  const store = useMemberAuthStore();

  // Arrived on a subdomain this org was renamed away from: the server still
  // resolves it, and reports where the portal actually lives now.
  useCanonicalPortalRedirect(data?.subdomain);

  const onToken = (state) => {
    redirect && redirect !== "/" ? navigate(redirect) : navigate(`/members`);
  };

  const onClick = (provider: string) => {
    openSignInWindow(signInRequestPath(provider, subdomain), "Login", onToken);
  };

  // A link like `?auth=microsoft` goes straight to that provider, when the org
  // offers it. Not from a subdomain the org was renamed away from: the
  // canonical redirect above takes the member to the right portal first.
  // The param is dropped first so Back lands on the buttons instead of
  // bouncing to the provider again.
  const autoProvider =
    data && data.subdomain === subdomain
      ? requestedProvider(search.get("auth"), data.availableLoginOptions)
      : null;
  useEffect(() => {
    if (!autoProvider || store.isSignedin) return;
    setSearch(
      (params) => {
        params.delete("auth");
        return params;
      },
      { replace: true },
    );
    rememberReturnPath(redirect);
    redirectToSignIn(signInRequestPath(autoProvider, subdomain));
  }, [autoProvider, store.isSignedin]);

  useEffect(() => {
    if (store.accessToken && store.isSignedin) {
      navigate("/members");
    }
  }, [store.accessToken, store.isSignedin]);

  if (!data) return <Text>Invalid portal</Text>;

  return (
    <Flex
      h="100vh"
      direction="column"
      align="center"
      bg="gray.50"
      px={[6, null, 10]}
      pt="13%" // this is what's controlling vertical position
    >
      <Flex w="full" maxW="sm" direction="column" gap={6}>
        <Flex alignSelf="center">
          {data.logoUrl ? (
            <Image src={data.logoUrl} maxW="200px" maxH="100px" />
          ) : (
            /* Fallback only — orgs with their own logo never see this. */
            <PloyLogoV2 />
          )}
        </Flex>

        <Card bg="white" shadow="sm" borderRadius="lg">
          <CardHeader textAlign="center" pb={2}>
            <Text fontSize="24px" fontWeight="500" color="gray.900">
              Welcome back
            </Text>
            <Text color="gray.500" fontSize="14px">
              {loginHeading(data.availableLoginOptions)}
            </Text>
          </CardHeader>

          <CardBody>
            <VStack spacing={4}>
              {offeredProviders(data.availableLoginOptions).map((provider) => (
                <Button
                  key={provider.id}
                  w="full"
                  variant="outline"
                  h="40px"
                  borderColor="gray.200"
                  leftIcon={<Icon as={provider.icon} />}
                  onClick={() => onClick(provider.id)}
                  _hover={{ bg: "gray.50" }}
                >
                  Login with {provider.name}
                </Button>
              ))}
            </VStack>
          </CardBody>
        </Card>
      </Flex>
    </Flex>
  );
};

export default SignIn;
